import { useCallback } from "react";
import { get as commsGet } from "../../../service/comms/commsApi";
import {
  getAgentsWorkload,
  listMacros,
  listTags,
} from "../../../service/comms/supportService";
import { useCachedList } from "../../../hooks/useCachedList";
import { supportVocabCache } from "../../../utils/listCache/caches";
import { normalizeAgent, toList } from "./supportUtils";

/**
 * Desk-wide reference data: tags, canned responses, and who is on shift.
 *
 * All three go through `useCachedList` over one shared namespace, which is
 * what stops the several components that need them each issuing their own
 * request — its in-flight map keys on `namespace:key`, so the tag filter in
 * the queue and the tag editor in the ticket header resolve from a single
 * call. They also paint from cache and revalidate quietly, rather than
 * re-fetching a vocabulary that changes maybe once a month.
 */

/**
 * Canned responses for the composer.
 *
 * A macro with no `department` shows in every pool, and the `?department=`
 * filter includes those — "Ask for a screenshot" is useful everywhere — so this
 * never needs a second unfiltered call.
 *
 * @param {string|null} [department] - Narrow to one pool's macros.
 * @returns {{ macros: Array<{ id?: string, title: string, body: string, shortcut?: string, department?: string }>, loading: boolean }}
 */
export function useSupportMacros(department = null) {
  const fetcher = useCallback(
    () =>
      listMacros(commsGet, department ? { department } : {}).then((payload) =>
        toList(payload, ["macros", "items", "data"])
      ),
    [department]
  );

  const { data, loading } = useCachedList({
    cache: supportVocabCache,
    key: `macros:${department || "all"}`,
    fetcher,
  });

  return { macros: data || [], loading };
}

/**
 * The company's tag vocabulary, for the filter and the ticket header.
 *
 * Tags used in a ticket `PATCH` are auto-created, so this list is a convenience
 * rather than a constraint — an agent can always type a new one, and `reload`
 * pulls it back in.
 *
 * @returns {{ tags: Array<{ name: string, color?: string }>, loading: boolean, reload: () => void }}
 */
export function useSupportTags() {
  const fetcher = useCallback(
    () => listTags(commsGet).then((payload) => toList(payload, ["tags", "items", "data"])),
    []
  );

  const { data, loading, refresh } = useCachedList({
    cache: supportVocabCache,
    key: "tags",
    fetcher,
  });

  return { tags: data || [], loading, reload: refresh };
}

/**
 * Agents on shift, with their load, capacity and declared departments.
 *
 * Read by the views rail's team strip, the escalate picker and the auto-assign
 * dialog. They used to fetch it three times over; sharing one cache key means
 * one request whoever asks first.
 *
 * @param {boolean} [enabled=true] - False defers the fetch entirely, for
 *   consumers that only need it once something opens.
 * @returns {{ agents: Array<Object>, loading: boolean, reload: () => void }}
 */
export function useAgentsWorkload(enabled = true) {
  const fetcher = useCallback(
    () =>
      getAgentsWorkload(commsGet).then((payload) =>
        toList(payload).map(normalizeAgent).filter((a) => a.userId)
      ),
    []
  );

  const { data, loading, refresh } = useCachedList({
    cache: supportVocabCache,
    key: "workload",
    fetcher,
    enabled,
  });

  return { agents: data || [], loading, reload: refresh };
}
